import { useQuery } from "@apollo/client/react";
import { UsageTrackerEvent, usageTracker } from "@volumental/analytic";
import { Container, ErrorText, isMobileDevice, Loader, Spinner } from "@volumental/common";
import { useCallback, useState } from "react";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";
import { graphql } from "../../api/gql";
import { GetScansQuery } from "../../api/queries/scan-query";
import { tokenStore } from "../../auth/token-store";
import { useDeviceAuth } from "../../auth/use-device-auth";
import { useInputStore, usePersistedViewStore, useScannerStore, useSessionStore, useViewStore } from "../../store";
import { getJourneyIdFromUrl } from "@volumental/journey-manager";
import { getScannerUrlWithCallbacks } from "../../util/get-scanner-url";
import { hashString } from "../../util/hash";
import { AppMode, CookieType, CurrentView, UserFlowType } from "../../types/types";
import { GeneralErrorPage } from "../error/GeneralErrorPage";
import { CookieModal } from "../scanner/cookie-modal/cookie-modal";
import { PrivacyFooter } from "./privacy-footer";
import { Header } from "../../components/accessible-components";
import { Button, Content, Headline, TextButton } from "@volumental/common";
import introBackground from "@volumental/assets/images/scanner/IntroPage.png";
import volumentalLogo from "@volumental/assets/logos/volumental.svg";

const GetIntroViewConfigurationQuery = graphql(`
  query getIntroViewConfigurationQuery {
    configuration {
      scanner {
        allowSelfScanning
        allowAssistedScanning
      }
      features {
        recommendation {
          enableSurvey
        }
        enableProfiles
      }
      logo
      heroImageUrl
      organizationName
    }
  }
`);

interface HeroIntroProps {
  onClose: () => void;
}

export function HeroIntro({ onClose }: HeroIntroProps): JSX.Element {
  const { loading, error, data } = useQuery(GetIntroViewConfigurationQuery);
  const { t, i18n } = useTranslation();
  const { mode, token, deviceId, session, paperType, skipOnboarding } = useSessionStore((store) => store);
  const { previousPurchaseID, subject, email } = useInputStore((store) => store);
  const { disableCaptureValidation } = useScannerStore((store) => store);
  usePersistedViewStore((store) => store);
  const { setCurrentView } = useViewStore((store) => store);
  const { cookieType } = usePersistedViewStore((store) => store);
  const [showCookieModal, setShowCookieModal] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const { refetch: fetchScans } = useQuery(GetScansQuery, {
    fetchPolicy: "network-only",
    skip: true,
  });

  const handleAuthSuccess = useCallback(() => {
    usageTracker.track(UsageTrackerEvent.VerifiedEmail);
    setCurrentView(CurrentView.HistorySelectScan);
  }, [setCurrentView]);

  const {
    startAuth,
    isPending,
    error: authError,
  } = useDeviceAuth({
    clientId: "visa-web-side-bar",
    onSuccess: handleAuthSuccess,
  });

  if (loading) {
    return <Loader />;
  }
  if (error || !data) {
    return <GeneralErrorPage errorText="Missing IntroView configuration" onClose={onClose} />;
  }

  const {
    scanner: { allowAssistedScanning, allowSelfScanning },
    features: {
      recommendation: { enableSurvey },
      enableProfiles,
    },
    logo,
    heroImageUrl,
    organizationName,
  } = data.configuration;

  const isVolumental = organizationName?.toLowerCase() === "volumental";

  const onNext = async (): Promise<void> => {
    usageTracker.track(UsageTrackerEvent.ContinueFromIntro);
    if (enableSurvey && mode === AppMode.Recommendation) {
      setCurrentView(CurrentView.StepListIntro);
    } else if (isMobileDevice() && !enableSurvey && allowSelfScanning && !allowAssistedScanning) {
      // Solo-only config on mobile: redirect to standalone scanner
      usageTracker.track(UsageTrackerEvent.IntroLoadSolo, {
        sizing_flow_type: UserFlowType.Solo,
      });
      const paperFormat = paperType === "us-letter" ? "letter" : (paperType ?? undefined);
      const integrationId = subject ? await hashString(subject) : undefined;
      globalThis.location.href = getScannerUrlWithCallbacks(UserFlowType.Solo, {
        clientId: token,
        deviceId: deviceId ?? "",
        currentUrl: globalThis.location.href,
        previousPurchaseId: previousPurchaseID ?? undefined,
        session,
        paperFormat: skipOnboarding && paperFormat ? paperFormat : undefined,
        skipValidation: disableCaptureValidation,
        language: i18n.language,
        integrationId,
        journeyId: getJourneyIdFromUrl(),
        email: email ?? undefined,
      });
    } else {
      setCurrentView(CurrentView.Picker);
    }
  };

  const onClickAction = (): void => {
    if (cookieType === CookieType.Decline) {
      setShowCookieModal(true);
    } else {
      void onNext();
    }
  };

  const onSignInAction = async (): Promise<void> => {
    if (isValidating) return;
    usageTracker.track(UsageTrackerEvent.SignInStart);
    if (tokenStore.getToken()) {
      setIsValidating(true);
      try {
        await fetchScans();
        handleAuthSuccess();
        return;
      } catch {
        tokenStore.clearToken();
      } finally {
        setIsValidating(false);
      }
    }
    void startAuth();
  };

  let alternativeTitle: React.ReactNode;
  if (isPending) {
    alternativeTitle = t("completeSignInInPopup");
  } else if (isValidating) {
    alternativeTitle = <Spinner size="14px" />;
  } else {
    alternativeTitle = t("accessMySavedResults");
  }

  const authErrorNode = authError ? (
    <ErrorText>{t(authError === "popup-blocked" ? "signInPopupBlocked" : "SomethingWentWrong")}</ErrorText>
  ) : undefined;

  return (
    <Container>
      <Header close={onClose}>
        <div className="flex gap-2 items-center pt-2">
          {logo && !isVolumental && (
            <>
              <img className="w-12 h-12 object-contain" src={logo} alt="logo" />
              <div className="w-px h-8 bg-black" />
            </>
          )}
          <img className="w-12 h-12 object-contain" src={volumentalLogo} alt="Volumental logo" />
        </div>
      </Header>
      <Content disableScrollbar>
        <div className="shrink-0 mt-4 mb-6">
          <div className="w-full aspect-video rounded-xl overflow-hidden">
            <img className="w-full h-full object-cover" src={heroImageUrl ?? introBackground} alt="Intro Background" />
          </div>
        </div>
        <Headline className="text-center">
          {mode === AppMode.Recommendation ? t("findYourBestSizeIntro") : t("scanYourFeet")}
        </Headline>
        <div className="w-full flex flex-col items-center mb-35 mt-6">
          <Button loading={false} type="submit" onClick={onClickAction}>
            {t("button.getStarted")}
          </Button>
          {enableProfiles ? (
            <div className="flex flex-col items-center gap-1 mt-2">
              <TextButton onClick={onSignInAction}>{alternativeTitle}</TextButton>
              {authErrorNode}
            </div>
          ) : undefined}
        </div>
      </Content>
      <PrivacyFooter />
      <CookieModal
        show={showCookieModal}
        close={() => {
          setShowCookieModal(false);
        }}
        next={onNext}
      />
    </Container>
  );
}
